import { TourStep } from "./types";

/** A step's words live in the common locale under tour.steps.<id>, in every language the app speaks. */
const copyOf = (id: string) => ({
  id,
  title: `tour.steps.${id}.title`,
  body: `tour.steps.${id}.body`,
});
const instructionOf = (id: string) => `tour.steps.${id}.instruction`;

const testId = (id: string) => `[data-testid="${id}"]`;
const tour = (id: string) => `[data-tour="${id}"]`;

const studioTab = `${tour("open-cdt-studio")}:not([aria-disabled="true"])`;
const studioPanel = testId("cdt-studio-panel");
const discardStudioButton = `[data-modal="ModalConfirm"] ${testId("cdt-studio-close-discard")}`;
const rulesModal = '[data-modal="ModalStorePlanogramRules"] .modal';
const oAdvanced = testId("o-advanced");
const withoutStudioNotice = testId("rules-notice-without-studio");
const verticalBlockedNotice = testId("rules-notice-vertical-blocked");
/** One of the two is up whenever the studio is not finalized, whichever blocking is chosen. */
const studioNotFinalizedNotice = `${withoutStudioNotice}, ${verticalBlockedNotice}`;
const sidepanelTabs = tour("sidepanel-tabs");
const generateButton = testId("generate-store-planograms");

/**
 * The exercise: one base planogram, from reading it to the point of generating
 * store planograms. It runs on the user's own data, and every step that could
 * save or generate something is shown, explained and kept out of reach.
 */
export const planogramExerciseSteps: TourStep[] = [
  {
    ...copyOf("welcome"),
  },
  {
    ...copyOf("flow-header"),
    target: tour("flow-header"),
    placement: "bottom",
  },
  {
    ...copyOf("primary-blocking"),
    target: testId("primary-blocking"),
    placement: "bottom",
    requires: testId("primary-blocking"),
  },
  {
    ...copyOf("flow-steps"),
    target: tour("flow-steps"),
    placement: "bottom",
  },
  {
    ...copyOf("planogram"),
    target: tour("planogram-area"),
    placement: "left",
    requires: sidepanelTabs,
  },
  {
    ...copyOf("sidepanel"),
    target: sidepanelTabs,
    placement: "left",
    requires: sidepanelTabs,
  },
  {
    ...copyOf("open-studio"),
    target: studioTab,
    placement: "left",
    requires: studioTab,
    settledBy: sidepanelTabs,
    task: {
      instruction: instructionOf("open-studio"),
      doneWhenPresent: studioPanel,
    },
  },
  {
    ...copyOf("studio-tree"),
    target: testId("cdt-studio-tree-workspace"),
    placement: "left",
    requires: studioPanel,
    settledBy: sidepanelTabs,
  },
  {
    ...copyOf("studio-findings"),
    target: testId("cdt-studio-findings-region"),
    placement: "left",
    requires: testId("cdt-studio-findings-region"),
    settledBy: sidepanelTabs,
  },
  {
    ...copyOf("studio-fix-blocking"),
    target: testId("cdt-studio-panel-fix-blocking"),
    placement: "bottom",
    requires: studioPanel,
    settledBy: sidepanelTabs,
  },
  {
    ...copyOf("studio-finalize"),
    target: testId("cdt-studio-panel-finalize"),
    placement: "bottom",
    requires: studioPanel,
    settledBy: sidepanelTabs,
  },
  {
    ...copyOf("close-studio"),
    target: testId("cdt-studio-panel-close"),
    placement: "bottom",
    requires: studioPanel,
    settledBy: sidepanelTabs,
    task: {
      instruction: instructionOf("close-studio"),
      // With unfinalized changes, Close asks first; that question is the next step.
      doneWhenPresent: discardStudioButton,
      doneWhenAbsent: studioPanel,
    },
  },
  {
    ...copyOf("discard-studio"),
    target: discardStudioButton,
    placement: "top",
    requires: discardStudioButton,
    settledBy: sidepanelTabs,
    confirmation: "ModalConfirm",
    task: {
      instruction: instructionOf("discard-studio"),
      doneWhenAbsent: studioPanel,
    },
  },
  {
    ...copyOf("metrics"),
    target: tour("planogram-metrics"),
    placement: "top",
    requires: tour("planogram-metrics"),
  },
  {
    ...copyOf("open-rules"),
    target: generateButton,
    placement: "top",
    task: {
      instruction: instructionOf("open-rules"),
      doneWhenPresent: rulesModal,
    },
  },
  {
    // The rules open on O; every rule the next steps point at is behind Advanced settings.
    ...copyOf("open-advanced"),
    target: oAdvanced,
    placement: "bottom",
    requires: oAdvanced,
    settledBy: rulesModal,
    task: {
      instruction: instructionOf("open-advanced"),
      doneWhenPresent: tour("rules-tabs"),
    },
  },
  {
    ...copyOf("rules-without-studio"),
    target: withoutStudioNotice,
    placement: "bottom",
    requires: withoutStudioNotice,
    settledBy: rulesModal,
  },
  {
    ...copyOf("try-vertical"),
    target: testId("rule-vertical-blocking"),
    placement: "left",
    requires: studioNotFinalizedNotice,
    settledBy: rulesModal,
    task: {
      instruction: instructionOf("try-vertical"),
      doneWhenPresent: verticalBlockedNotice,
    },
  },
  {
    ...copyOf("stay-horizontal"),
    target: verticalBlockedNotice,
    placement: "bottom",
    requires: verticalBlockedNotice,
    settledBy: rulesModal,
    task: {
      instruction: instructionOf("stay-horizontal"),
      doneWhenAbsent: verticalBlockedNotice,
    },
  },
  {
    ...copyOf("rules-tabs"),
    target: tour("rules-tabs"),
    placement: "bottom",
    requires: rulesModal,
    settledBy: generateButton,
  },
  {
    ...copyOf("rules-footer"),
    target: tour("rules-footer"),
    placement: "top",
    requires: rulesModal,
    settledBy: generateButton,
  },
  {
    ...copyOf("rules-generate"),
    target: testId("rules-generate"),
    placement: "top",
    requires: rulesModal,
    settledBy: generateButton,
  },
  {
    ...copyOf("close-rules"),
    target: testId("rules-cancel"),
    placement: "top",
    requires: rulesModal,
    settledBy: generateButton,
    task: {
      instruction: instructionOf("close-rules"),
      doneWhenAbsent: rulesModal,
    },
  },
  {
    ...copyOf("done"),
  },
];

/** Shown instead of the exercise when the user has no planogram to run it on. */
export const noPlanogramSteps: TourStep[] = [
  {
    ...copyOf("no-planogram"),
  },
];
